import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  sendPasswordResetEmail,
} from "firebase/auth";
import { auth } from "@app/config/firebase";
import { AuthContextType, User } from "@shared/lib/types/auth";
import { formatUser } from "@shared/lib/utils/formatUser";
import { useGetBeUser } from "../hooks/useGetUser";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@shared/lib/api";
import { BackednResponse, UserDetailsResponse } from "@shared/lib/types";

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const { beUser, isLoading: isBeUserLoading } = useGetBeUser(Boolean(user));

  const queryClient = useQueryClient();

  const signup = async (email: string, password: string): Promise<void> => {
    const { user: firebaseUser } = await createUserWithEmailAndPassword(
      auth,
      email,
      password
    );
    setUser(formatUser(firebaseUser));
  };

  const login = async (email: string, password: string): Promise<void> => {
    const { user: firebaseUser } = await signInWithEmailAndPassword(
      auth,
      email,
      password
    );
    setUser(formatUser(firebaseUser));
  };

  const googleSignIn = async (): Promise<void> => {
    const provider = new GoogleAuthProvider();
    const { user: firebaseUser } = await signInWithPopup(auth, provider);
    setUser(formatUser(firebaseUser));
  };

  const resetPassword = async (email: string): Promise<void> => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      console.error("Error resetting password : ", error);
      throw error;
    }
  };

  const sendMFAOTP = async (): Promise<
    BackednResponse<{ mfaCode: string }>
  > => {
    return (await api
      .post("user/send-mfa-otp", {
        json: {},
      })
      .json()) as BackednResponse<{
      mfaCode: string;
    }>;
  };

  const verifyMFAOTP = async (otp: string, mfaCode: string): Promise<void> => {
    await api.post("user/validate-mfa-otp", {
      json: {
        mfaCode: mfaCode,
        otp: otp,
      },
    });
  };

  const getMe = async () => {
    const url = `user/me`;
    const response = (await api
      .get(url)
      .json()) as BackednResponse<UserDetailsResponse>;
    return response;
  };

  const logout = async (): Promise<void> => {
    await signOut(auth);
    queryClient.removeQueries();
    setUser(null);
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser ? formatUser(firebaseUser) : null);
      if (!firebaseUser) queryClient.removeQueries();
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    if (user) {
      localStorage.setItem("user", JSON.stringify(user));
    } else {
      localStorage.removeItem("user");
    }
  }, [user]);

  const value: AuthContextType = {
    user,
    loading,
    signup,
    login,
    googleSignIn,
    logout,
    resetPassword,
    isRishabEmail: false,
    isDushyantEmail: (user?.email || "") === "dushyant.sharma@cloutflow.com",
    beUser: beUser || null,
    isBeUserLoading,
    sendMFAOTP,
    verifyMFAOTP,
    getMe,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
